/**
 * O roteiro da demonstração — DADO, não código.
 *
 * A ordem, o texto e o que a seta aponta ficam todos aqui, num array que se lê
 * de cima a baixo. Quem executa é `Demonstracao.jsx`, e ele não sabe nada sobre
 * o escritório: recebe passos e obedece. Foi assim de propósito — o roteiro do
 * agente (`src/astrea.ts:469`) era código espalhado por um `try`, e mudar a
 * ordem de duas explicações mexia no motor.
 *
 * O que ESTÁ escrito aqui é caminho de tela: `aria-label` do trilho, nome de
 * campo, chave de query. Isso muda em deploy, não em operação. O que NÃO está
 * aqui é etapa do funil, nome de verba nem lista de opções — esse dado é
 * configurável em tempo de execução e a demonstração o mostra vindo do banco,
 * como qualquer outra tela.
 *
 * Nenhum passo clica em nada (o ponteiro não tem `click`), e o único que mexe em
 * dado da mesa é o de digitar, que semeia o rascunho local e para antes do
 * envio. Enviar continua sendo a mão da pessoa.
 *
 * O ALVO é `[data-secao=…]` da coluna do atendimento, e isso é o que sobrou de
 * uma correção: dois passos apontavam `[role="tablist"]` das abas "Caso" e
 * "Reunião", que morreram quando o painel virou uma coluna só. `querySelector`
 * devolvia `null`, o motor tratava como passo sem alvo e a seta recolhia — a
 * apresentação do sistema perdia a seta em dois dos seis passos sem dizer nada a
 * ninguém. Por isso `roteiro.test.jsx` monta a coluna e cobra cada alvo.
 */

export type Passo = {
  /** O que o HUD mostra em negrito, e o rótulo colado na seta. */
  titulo: string;
  /** A frase que explica o passo. É o texto que a pessoa lê. */
  dica: string;
  /** Seletor do que a seta aponta. Sem ele o passo é só explicação. */
  alvo?: string;
  /** Leva a mesa a um LUGAR antes de apontar (a demonstração pode começar em qualquer tela). */
  ir?: string;
  /** Abre o atendimento que a demonstração escolheu. */
  abrirCaso?: boolean;
  /** Recorte da mesma tela: vira query (`?painel=`, `?peticao=`). `null` tira o parâmetro. */
  query?: Record<string, string | boolean | null>;
  /** Semeia o compositor. Nunca envia. */
  digitar?: string;
  /** Quanto o passo fica na tela, em ms. Sem isto, o padrão do motor. */
  demora?: number;
};

const EXEMPLO =
  "Boa tarde! Sou da Galera Advocacia e vou cuidar do seu caso. " +
  "Para dar entrada preciso do RG, CPF, comprovante de residência e a carteira de trabalho — pode mandar foto por aqui.";

/*
 * Frases CURTAS: o HUD é uma pílula de uma linha no computador e duas no
 * celular. O que não cabe numa frase é capítulo da ajuda, não passo de
 * demonstração — e o último passo diz onde a ajuda está.
 */
export const PASSOS: Passo[] = [
  {
    titulo: "A mesa",
    dica: "Toda conversa do setor entra nesta lista — WhatsApp, anúncio ou planilha. Quem espera resposta vem primeiro.",
    ir: "/atendimentos",
    alvo: "[data-secao='lista']",
  },
  {
    titulo: "Tarefas e agenda",
    dica: "Prazo, reunião marcada e mensagem agendada no mesmo quadro. O sino conta só o que é seu.",
    alvo: '[aria-label="Tarefas e agenda"]',
  },
  {
    titulo: "Quanto a IA faz",
    dica: "O setor escolhe um modo: Sem IA, Copiloto ou Piloto. Os sete agentes já vêm escritos — cada um com gatilho e prompt.",
    alvo: '[aria-label="IA do atendimento"]',
  },
  {
    titulo: "Um atendimento",
    dica: "Abrir a conversa traz junto a ficha, os documentos, os prazos e a petição.",
    abrirCaso: true,
  },
  {
    titulo: "O que fazer agora",
    dica: "O cartão AGORA é o próximo passo que o sistema calculou para este caso. É dele que o caso anda.",
    /* A seção pela URL, e não "1": o AGORA mora na aba Caso (29/10), e é o
       `?painel=` que escolhe a aba. */
    query: { painel: "agora" },
    alvo: "[data-secao='agora']",
  },
  {
    titulo: "A leitura da IA",
    dica: "Uma linha com o que a IA entendeu. Prazo e documento que falta saem do banco, nunca do modelo.",
    query: { painel: "1" },
    alvo: "[data-secao='copiloto']",
  },
  {
    titulo: "A ficha do caso",
    dica: "Os campos são os do setor e editam no lugar. O que a petição precisa e está vazio aparece para preencher.",
    query: { painel: "ficha" },
    alvo: "[data-bloco='ficha']",
  },
  {
    titulo: "A resposta",
    dica: "O texto entra no rascunho e para aí. Enviar é a sua mão, no Enter — nada sai para o cliente nesta demonstração.",
    alvo: '[aria-label="Mensagem para o cliente"]',
    /* Tira o `?painel=` dos passos anteriores: no celular a ficha é GAVETA e
       ficava por cima do campo que este passo aponta. No computador a coluna
       continua — lá quem manda nela é a largura, não o endereço. */
    query: { painel: null },
    digitar: EXEMPLO,
    demora: 4200,
  },
  {
    titulo: "A petição nascendo",
    dica: "O fato marcado na conversa é o mesmo que entra na seção de fatos da inicial — um dado só.",
    query: { peticao: true },
    demora: 4200,
  },
];

/** Passo que só faz sentido de dentro de uma conversa aberta. */
const precisaDeCaso = (p: Passo) => Boolean(p.abrirCaso || p.query || p.digitar);

/**
 * O roteiro para esta situação.
 *
 * Sem atendimento escolhido (setor novo, mesa vazia) sobra o passeio pelas
 * áreas da mesa, e o fim diz como ver o resto. Era o contrário: a demonstração
 * recusava começar — e o setor novo, que é quem mais precisa dela, era
 * justamente o que não tinha caso.
 */
export function montarRoteiro(casoId: number | null): Passo[] {
  return casoId ? PASSOS : PASSOS.filter((p) => !precisaDeCaso(p));
}

/**
 * Ação que grava, e por isso não pode virar alvo.
 *
 * Hoje o ponteiro só aponta, então apontar "Enviar" seria mentira e não
 * estrago. O dia em que alguém der um `click()` a ele — e a tentação existe,
 * está em `src/astrea.ts:432` — é este teste que segura a porta.
 */
export const ALVO_PROIBIDO = /enviar|apagar|excluir|delegar|aprovar|transferir|arquivar|mover/i;

/**
 * Qual atendimento a demonstração usa — e por que não é "o primeiro da lista".
 *
 * Abrir uma conversa dispara `api.marcarLida` (estado.jsx), que é um UPDATE no
 * banco do escritório. Numa demonstração isso é gravar por acidente: a pessoa
 * mostrou o sistema e apagou a bolinha de não lida de um cliente de verdade.
 * Então: o caso já aberto (a leitura já foi marcada por quem o abriu) ou o
 * primeiro sem mensagem nova, onde o UPDATE não acha linha para mexer.
 */
export function escolherCaso(mesa: { id: number; nao_lidas?: number }[], casoAberto: number | null): number | null {
  if (casoAberto) return casoAberto;
  return mesa.find((c) => !c.nao_lidas)?.id ?? null;
}
